import { authorizedFetch, useAuth } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Query } from '@tanstack/react-query'

export type HealthRecordKind = 'medical_record' | 'test_result'
export type HealthRecordType =
  | 'general_document'
  | 'test_result'
  | 'imaging'
  | 'referral'
  | 'prescription'
  | 'form'
  | 'correspondence'
  | 'receipt_or_invoice'
  | 'unknown'

export type ClassificationStatus = 'pending' | 'processing' | 'classified' | 'needs_review' | 'unsupported' | 'failed'

export interface HealthRecordFile {
  id: string
  original_name: string
  mime_type: string
  size_bytes: number
  status: string
  file_url?: string
  parser_note?: string
  has_extracted_text?: boolean
  extracted_preview?: string
}

export interface HealthRecordAttachment {
  id: string
  role: string
  sort_order: number
  file: HealthRecordFile
}

export interface HealthRecordItem {
  id: string
  record_type: HealthRecordType
  record_type_label: string
  classification_status: ClassificationStatus
  classification_confidence?: number | null
  classification_reason: string
  processing_error: string
  processed_at?: string | null
  ingestion_batch_id?: string | null
  title: string
  description: string
  summary: string
  record_date?: string | null
  structured_extraction: Record<string, unknown>
  fhir_hints: Record<string, unknown>
  visibility: string
  source: string
  attachment_count: number
  attachments: HealthRecordAttachment[]
  created_at: string
  updated_at: string
}

export type HealthRecord = HealthRecordItem

export interface HealthRecordFilters {
  record_type?: HealthRecordType
  classification_status?: ClassificationStatus
  source?: string
  search?: string
  limit?: number
}

export interface CreateHealthRecordData {
  record_type?: HealthRecordType
  title: string
  description?: string
  record_date?: string
  classification_status?: ClassificationStatus
  file_asset_ids?: number[]
}

export interface UpdateHealthRecordData {
  id: string
  title?: string
  description?: string
  summary?: string
  record_type?: HealthRecordType
  record_date?: string | null
  classification_status?: ClassificationStatus
  classification_confidence?: number | null
  classification_reason?: string
  processing_error?: string
  structured_extraction?: Record<string, unknown>
  fhir_hints?: Record<string, unknown>
  visibility?: string
}

export interface HealthRecordIngestion {
  id: string
  source: string
  user_hint: string
  thread_id: string
  status: string
  processing_error: string
  file_asset_ids: string[]
  records: HealthRecordItem[]
  created_at: string
  updated_at: string
  processed_at?: string | null
}

export interface CreateHealthRecordIngestionData {
  file_asset_ids: string[]
  user_hint?: string
  source?: string
  thread_id?: string
}

export interface UploadedFileAsset {
  id: string
  original_name: string
  mime_type: string
  size_bytes: number
  status: string
  file_url?: string
}

/** Ingestion runs in a background thread; poll until Lumie leaves pipeline states. */
const HEALTH_RECORD_PIPELINE_POLL_MS = 3000

function listHasPipelineRecords(data: HealthRecordItem[] | undefined): boolean {
  return !!data?.some(
    (r) => r.classification_status === 'pending' || r.classification_status === 'processing',
  )
}

function recordListRefetchInterval(
  query: Query<HealthRecordItem[], Error, HealthRecordItem[], readonly unknown[]>,
): number | false {
  return listHasPipelineRecords(query.state.data)
    ? HEALTH_RECORD_PIPELINE_POLL_MS
    : false
}

function singleRecordRefetchInterval(
  query: Query<HealthRecordItem, Error, HealthRecordItem, readonly unknown[]>,
): number | false {
  const record = query.state.data
  if (!record) return false
  if (record.classification_status === 'pending' || record.classification_status === 'processing') {
    return HEALTH_RECORD_PIPELINE_POLL_MS
  }
  return false
}

export function useHealthRecords(filters: HealthRecordFilters = {}, enabled: boolean = true) {
  const { token } = useAuth()

  const query = useQuery<HealthRecordItem[], Error>({
    queryKey: ['health-records', filters],
    queryFn: async (): Promise<HealthRecordItem[]> => {
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (filters.record_type) params.set('record_type', filters.record_type)
      if (filters.classification_status) params.set('classification_status', filters.classification_status)
      if (filters.source) params.set('source', filters.source)
      if (filters.search?.trim()) params.set('search', filters.search.trim())
      if (filters.limit) params.set('limit', String(filters.limit))

      const response = await authorizedFetch(`/api/health-records/${params.toString() ? `?${params}` : ''}`)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to fetch health records')
      }
      return response.json()
    },
    enabled: !!token && enabled,
    staleTime: 30 * 1000,
    refetchInterval: recordListRefetchInterval,
  })

  return {
    records: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useHealthRecord(recordId?: string) {
  const { token } = useAuth()

  const query = useQuery<HealthRecordItem, Error>({
    queryKey: ['health-record', recordId],
    queryFn: async (): Promise<HealthRecordItem> => {
      if (!token) throw new Error('Not authenticated')
      if (!recordId) throw new Error('Missing health record id')

      const response = await authorizedFetch(`/api/health-records/${recordId}`)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to fetch health record')
      }
      return response.json()
    },
    enabled: !!token && !!recordId,
    staleTime: 30 * 1000,
    refetchInterval: singleRecordRefetchInterval,
  })

  return {
    record: query.data || null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useCreateHealthRecordIngestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateHealthRecordIngestionData): Promise<HealthRecordIngestion> => {
      const response = await authorizedFetch('/api/health-records/ingestions', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to file health records')
      }
      return response.json()
    },
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ['health-records'] })
      for (const record of batch.records) {
        queryClient.invalidateQueries({ queryKey: ['health-record', record.id] })
      }
    },
  })
}

export function useCreateHealthRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateHealthRecordData): Promise<HealthRecordItem> => {
      const response = await authorizedFetch('/api/health-records/', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to create health record')
      }
      return response.json()
    },
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: ['health-records'] })
      queryClient.invalidateQueries({ queryKey: ['health-record', record.id] })
    },
  })
}

export function useUpdateHealthRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateHealthRecordData): Promise<HealthRecordItem> => {
      const response = await authorizedFetch(`/api/health-records/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update health record')
      }
      return response.json()
    },
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: ['health-records'] })
      queryClient.invalidateQueries({ queryKey: ['health-record', record.id] })
    },
  })
}

export function useDeleteHealthRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await authorizedFetch(`/api/health-records/${id}`, { method: 'DELETE' })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete health record')
      }
    },
    onSuccess: (_void, id) => {
      queryClient.invalidateQueries({ queryKey: ['health-records'] })
      queryClient.invalidateQueries({ queryKey: ['health-record', id] })
    },
  })
}

export async function uploadHealthRecordFile(file: File): Promise<UploadedFileAsset> {
  const fd = new FormData()
  fd.append('file', file)
  const response = await authorizedFetch('/api/files/upload', {
    method: 'POST',
    body: fd,
  })
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || 'Failed to upload file')
  }
  return response.json()
}
